import { startTransition, useEffect, useState } from 'react';
import type { FilterParamsRecord } from '@/shared/utils/filterHelpers';
import { convertUrlParamsToFilters, createFilterRemover } from '@/shared/utils/filterHelpers';

interface UseEntityManagementOptions<TEntity, TFilters, TApiParams> {
    querySync: {
        params: Record<string, unknown>;
        apiParams: TApiParams;
        applyFilters: (filters: Partial<TFilters>) => void;
        setPage: (page: number) => void;
        setPageSize?: (size: number) => void;
    };

    entities: TEntity[];
    totalCount: number;
    isLoading: boolean;
    error: Error | null;

    onFiltersChange?: (filters: TFilters) => void;
    onPageChange?: (page: number) => void;
}

interface UseEntityManagementReturn<TEntity, TFilters> {
    entities: TEntity[];
    totalCount: number;
    isLoading: boolean;
    error: Error | null;

    currentPage: number;
    totalPages: number;
    pageSize: number;
    handlePageChange: (page: number) => void;
    handlePageSizeChange?: (size: number) => void;

    appliedFilters: TFilters;
    isFilterDrawerOpen: boolean;
    handleApplyFilters: (filters: Partial<TFilters>) => void;
    handleRemoveFilter: (key: keyof TFilters, value?: string | number) => void;
    handleClearFilters: () => void;
    toggleFilterDrawer: () => void;
    openFilterDrawer: () => void;
    closeFilterDrawer: () => void;
}

export function useEntityManagement<TEntity, TFilters extends Record<string, unknown>, TApiParams = unknown>({
    querySync,
    entities,
    totalCount,
    isLoading,
    error,
    onFiltersChange,
    onPageChange: onPageChangeCallback,
}: UseEntityManagementOptions<TEntity, TFilters, TApiParams>): UseEntityManagementReturn<TEntity, TFilters> {
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

    const [appliedFilters, setAppliedFilters] = useState<TFilters>({} as TFilters);

    useEffect(() => {
        const newFilters = convertUrlParamsToFilters<TFilters>(querySync.params as FilterParamsRecord);
        startTransition(() => {
            setAppliedFilters(prevFilters => {
                const filtersChanged = JSON.stringify(prevFilters) !== JSON.stringify(newFilters);
                if (filtersChanged) {
                    onFiltersChange?.(newFilters);
                    return newFilters;
                }
                return prevFilters;
            });
        });
    }, [querySync.params, onFiltersChange]);

    const handleRemoveFilter = (key: keyof TFilters, value?: string | number) => {
        startTransition(() => {
            const remover = createFilterRemover({
                convertUrlParamsToFilters: (urlParams: FilterParamsRecord) => convertUrlParamsToFilters<TFilters>(urlParams),
                applyFilters: querySync.applyFilters,
                currentParams: querySync.params as FilterParamsRecord,
            });
            remover(key as string, value);
        });
    };

    const handleClearFilters = () => {
        startTransition(() => {
            querySync.applyFilters({});
        });
    };

    const handleApplyFilters = (filters: Partial<TFilters>) => {
        querySync.applyFilters(filters);
        setIsFilterDrawerOpen(false);
    };

    const toggleFilterDrawer = () => {
        setIsFilterDrawerOpen(prev => !prev);
    };

    const openFilterDrawer = () => {
        setIsFilterDrawerOpen(true);
    };

    const closeFilterDrawer = () => {
        setIsFilterDrawerOpen(false);
    };

    const pageSize = Number(querySync.params.size) || 25;
    const currentPage = Number(querySync.params.page) || 1;
    const totalPages = Math.ceil(totalCount / pageSize);

    const handlePageChange = (page: number) => {
        querySync.setPage(page);
        onPageChangeCallback?.(page);
    };

    const handlePageSizeChange = querySync.setPageSize
        ? (size: number) => {
              querySync.setPageSize?.(size);
              querySync.setPage(1);
          }
        : undefined;

    return {
        entities,
        totalCount,
        isLoading,
        error,

        currentPage,
        totalPages,
        pageSize,
        handlePageChange,
        handlePageSizeChange,

        appliedFilters,
        isFilterDrawerOpen,
        handleApplyFilters,
        handleRemoveFilter,
        handleClearFilters,
        toggleFilterDrawer,
        openFilterDrawer,
        closeFilterDrawer,
    };
}
